import React from "react";
import styled from "styled-components";
import Page from "../../components/Page";
import Async from "../../components/Async";
import api from "../../services/api";
import { Button } from "../../components/buttons";
import { borderColor, spacing } from "../../components/layout";

const RECENT_LIMIT = 40;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${spacing};
  margin-bottom: 20px;
`;

const ToolbarText = styled.div`
  max-width: 760px;
  line-height: 1.45;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const Muted = styled.div`
  color: var(--muted-text);
  font-size: 13px;
  margin-top: 4px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${spacing};
  margin-bottom: 24px;
`;

const SummaryCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 16px;
  background: var(--surface-bg);
`;

const SummaryLabel = styled.div`
  color: var(--muted-text);
  font-size: 13px;
`;

const SummaryValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  margin-top: 6px;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${spacing};
  margin-bottom: 26px;
`;

const SectionCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 16px;
  background: var(--surface-bg);
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-weight: 700;
  margin-bottom: 14px;
`;

const SearchBarRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
`;

const SearchInput = styled.input`
  min-width: 320px;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid ${borderColor};
  background: var(--surface-bg);
  color: var(--text-color);
  outline: none;

  &::placeholder {
    color: var(--muted-text);
  }
`;

const BreakdownRow = styled.div`
  display: grid;
  grid-template-columns: minmax(90px, 1fr) 70px;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const BreakdownLabel = styled.div`
  min-width: 0;
`;

const BreakdownLabelText = styled.div`
  font-size: 13px;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BreakdownBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 999px;
  overflow: hidden;
`;

const BreakdownFill = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background: #54d2b1;
  border-radius: 999px;
`;

const BreakdownCount = styled.div`
  text-align: right;
  font-weight: 600;
`;

const EmptyState = styled.div`
  color: var(--muted-text);
  font-size: 13px;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing};
`;

const ItemCard = styled.details`
  border: 1px solid ${borderColor};
  border-radius: 10px;
  background: var(--surface-bg);
  overflow: hidden;
`;

const ItemSummary = styled.summary`
  list-style: none;
  cursor: pointer;
  padding: 16px;
  display: flex;
  gap: 14px;
  align-items: flex-start;

  &::-webkit-details-marker {
    display: none;
  }
`;

const UserThumb = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: #f3f3f3;
  overflow: hidden;
  flex: 0 0 52px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const SummaryBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
`;

const ItemTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${props => props.background || "rgba(0,0,0,0.07)"};
  color: ${props => props.color || "#1d1d1d"};
`;

const SummaryMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: var(--muted-text);
  font-size: 13px;
`;

const ItemBody = styled.div`
  border-top: 1px solid ${borderColor};
  padding: 16px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const MetaCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 12px;
  background: var(--surface-bg);
`;

const MetaLabel = styled.div`
  font-size: 12px;
  color: var(--muted-text);
  margin-bottom: 6px;
`;

const MetaValue = styled.div`
  font-weight: 600;
  line-height: 1.4;
  word-break: break-word;
`;

const LinkedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
`;

const LinkedCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 12px;
  background: var(--surface-bg);
`;

const SmallText = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: var(--muted-text);
  margin-top: 8px;
`;

const prettyLabel = value => {
  if (!value) {
    return "Unknown";
  }
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());
};

const formatDateTime = value => {
  if (!value) {
    return " - ";
  }
  return new Date(value).toLocaleString();
};

const compactList = values => {
  const filtered = values.filter(Boolean);
  return filtered.length ? filtered.join(" | ") : " - ";
};

const meetupTitle = meetup => {
  if (!meetup) {
    return "Later meetup";
  }
  return compactList([
    meetup.activity || prettyLabel(meetup.intent),
    meetup.location && meetup.location.label,
    meetup.plan_type && prettyLabel(meetup.plan_type)
  ]);
};

const locationLabel = meetup => {
  if (!meetup || !meetup.location) {
    return "No location";
  }
  const location = meetup.location;
  if (location.label) {
    return location.label;
  }
  if (typeof location.lat === "number" && typeof location.lng === "number") {
    return `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`;
  }
  return "No location";
};

const sourceColors = {
  organic: { background: "rgba(59, 130, 246, 0.16)", color: "#1d4ed8" },
  signal: { background: "rgba(20, 184, 166, 0.16)", color: "#0f766e" },
  seeded: { background: "rgba(245, 158, 11, 0.18)", color: "#92400e" }
};

const statusColors = {
  converted: { background: "rgba(52, 211, 153, 0.16)", color: "#047857" },
  confirmed: { background: "rgba(52, 211, 153, 0.16)", color: "#047857" },
  interested: { background: "rgba(20, 184, 166, 0.16)", color: "#0f766e" },
  surfaced: { background: "rgba(59, 130, 246, 0.16)", color: "#1d4ed8" },
  queued: { background: "rgba(107, 114, 128, 0.16)", color: "#374151" },
  candidate: { background: "rgba(107, 114, 128, 0.16)", color: "#374151" },
  dismissed: { background: "rgba(239, 68, 68, 0.15)", color: "#b91c1c" },
  expired: { background: "rgba(0, 0, 0, 0.08)", color: "#4b5563" },
  inactive: { background: "rgba(239, 68, 68, 0.15)", color: "#b91c1c" },
  upcoming: { background: "rgba(245, 158, 11, 0.18)", color: "#92400e" },
  in_window: { background: "rgba(59, 130, 246, 0.16)", color: "#1d4ed8" },
  live_activated: { background: "rgba(16, 185, 129, 0.18)", color: "#047857" },
  activated: { background: "rgba(14, 165, 233, 0.16)", color: "#0369a1" }
};

const intentColors = {
  friends: { background: "rgba(59, 130, 246, 0.16)", color: "#1d4ed8" },
  dates: { background: "rgba(244, 114, 182, 0.18)", color: "#be185d" },
  fun: { background: "rgba(245, 158, 11, 0.18)", color: "#92400e" }
};

const pillStyle = (map, key, fallback) => {
  const normalized = key ? String(key).toLowerCase() : "";
  return map[normalized] || fallback || null;
};

const BreakdownList = ({ title, items }) => {
  const maxCount = items.reduce((max, item) => Math.max(max, item.count), 0);

  return (
    <SectionCard>
      <SectionTitle>{title}</SectionTitle>
      {!items.length ? (
        <EmptyState>No data yet.</EmptyState>
      ) : (
        items.map(item => (
          <BreakdownRow key={`${title}-${item.label}`}>
            <BreakdownLabel>
              <BreakdownLabelText>{prettyLabel(item.label)}</BreakdownLabelText>
              <BreakdownBar>
                <BreakdownFill
                  width={maxCount ? Math.max((item.count / maxCount) * 100, 6) : 0}
                />
              </BreakdownBar>
            </BreakdownLabel>
            <BreakdownCount>{item.count}</BreakdownCount>
          </BreakdownRow>
        ))
      )}
    </SectionCard>
  );
};

const SourcePill = ({ source, family }) => {
  const style = pillStyle(sourceColors, family || source, null);
  return (
    <Pill background={style && style.background} color={style && style.color}>
      {prettyLabel(source || family)}
    </Pill>
  );
};

const StatusPill = ({ status }) => {
  const style = pillStyle(statusColors, status, null);
  return (
    <Pill background={style && style.background} color={style && style.color}>
      {prettyLabel(status)}
    </Pill>
  );
};

const IntentPill = ({ intent }) => {
  const style = pillStyle(intentColors, intent, null);
  if (!intent) {
    return null;
  }
  return (
    <Pill background={style && style.background} color={style && style.color}>
      {prettyLabel(intent)}
    </Pill>
  );
};

const ParticipantSummary = ({ participants }) => {
  const names = (participants || []).map(participant => {
    const suffix = participant.state && participant.state !== "pending"
      ? ` (${prettyLabel(participant.state)})`
      : "";
    return `${participant.name || `User ${participant.user_id}`}${suffix}`;
  });
  return names.length ? names.join(", ") : "No participants";
};

const SourcePlansSummary = ({ plans }) => {
  const sourcePlans = plans || [];
  if (!sourcePlans.length) {
    return <SmallText>No linked source plans.</SmallText>;
  }
  return (
    <SmallText>
      {sourcePlans.map(plan => (
        <div key={plan.id}>
          #{plan.id} {prettyLabel(plan.source)}:{" "}
          {compactList([
            plan.activity || prettyLabel(plan.intent),
            plan.destination_name,
            plan.source_summary
          ])}
        </div>
      ))}
    </SmallText>
  );
};

const LinkedMeetupCard = ({ meetup }) => {
  const candidate = meetup.candidate || {};
  return (
    <LinkedCard>
      <TitleRow>
        <ItemTitle>AI meetup #{meetup.id}</ItemTitle>
        <StatusPill status={meetup.status} />
        {candidate.source ? <SourcePill source={candidate.source} /> : null}
      </TitleRow>
      <SmallText>
        {compactList([
          meetupTitle(meetup),
          formatDateTime(meetup.meetup_time),
          locationLabel(meetup)
        ])}
      </SmallText>
      <SmallText>
        <ParticipantSummary participants={meetup.participants || []} />
      </SmallText>
    </LinkedCard>
  );
};

const RecentPlanItem = ({ plan, defaultOpen }) => {
  const user = plan.user || {};
  const stateStyle = pillStyle(statusColors, plan.state, null);

  return (
    <ItemCard open={defaultOpen}>
      <ItemSummary>
        <UserThumb>
          {user.profile_thumbnail_url ? <img src={user.profile_thumbnail_url} alt={user.name || "User"} /> : null}
        </UserThumb>
        <SummaryBody>
          <TitleRow>
            <ItemTitle>
              Plan #{plan.id} {user.name || `User ${user.id}`}
            </ItemTitle>
            <SourcePill source={plan.source} family={plan.source_family} />
            <Pill background={stateStyle && stateStyle.background} color={stateStyle && stateStyle.color}>
              {prettyLabel(plan.state)}
            </Pill>
            <IntentPill intent={plan.intent} />
            {plan.plan_type ? <Pill>{prettyLabel(plan.plan_type)}</Pill> : null}
          </TitleRow>
          <SummaryMeta>
            <div>Created {formatDateTime(plan.created_at)}</div>
            <div>
              {formatDateTime(plan.starts_at)} to {formatDateTime(plan.ends_at)}
            </div>
            <div>{plan.activity || plan.destination_name || plan.source_summary || "No activity"}</div>
            <div>{plan.linked_ai_meetup_count || 0} linked AI meetups</div>
          </SummaryMeta>
        </SummaryBody>
      </ItemSummary>

      <ItemBody>
        <MetaGrid>
          <MetaCard>
            <MetaLabel>User</MetaLabel>
            <MetaValue>
              {user.name || `User ${user.id || "-"}`}
              {user.id ? ` | ID ${user.id}` : ""}
              {user.age ? ` | ${user.age}` : ""}
              {user.approved ? " | Approved" : " | Not approved"}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Source</MetaLabel>
            <MetaValue>
              {prettyLabel(plan.source)} ({prettyLabel(plan.source_family)})
              {plan.source_summary ? ` | ${plan.source_summary}` : ""}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Intent / Plan</MetaLabel>
            <MetaValue>
              {prettyLabel(plan.intent)} | {prettyLabel(plan.plan_type)}
              {plan.moment ? ` | ${prettyLabel(plan.moment)}` : ""}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Activity / Logistics</MetaLabel>
            <MetaValue>
              {compactList([plan.activity, plan.logistics])}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Destination</MetaLabel>
            <MetaValue>
              {compactList([
                plan.destination_name,
                typeof plan.destination_lat === "number" && typeof plan.destination_lng === "number"
                  ? `${plan.destination_lat.toFixed(4)}, ${plan.destination_lng.toFixed(4)}`
                  : null
              ])}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Linked meetups</MetaLabel>
            <MetaValue>
              {plan.linked_ai_meetup_count || 0} total | {plan.active_linked_ai_meetup_count || 0} active
            </MetaValue>
          </MetaCard>
        </MetaGrid>

        {plan.seed_metadata ? (
          <SmallText>
            Metadata: {Object.keys(plan.seed_metadata).map(key => `${key}: ${plan.seed_metadata[key]}`).join(" | ")}
          </SmallText>
        ) : null}

        {!plan.linked_ai_meetups || !plan.linked_ai_meetups.length ? (
          <EmptyState>No linked AI meetups yet.</EmptyState>
        ) : (
          <LinkedGrid>
            {plan.linked_ai_meetups.map(meetup => (
              <LinkedMeetupCard key={meetup.id} meetup={meetup} />
            ))}
          </LinkedGrid>
        )}
      </ItemBody>
    </ItemCard>
  );
};

const RecentMeetupItem = ({ meetup, defaultOpen }) => {
  const candidate = meetup.candidate || {};
  return (
    <ItemCard open={defaultOpen}>
      <ItemSummary>
        <SummaryBody>
          <TitleRow>
            <ItemTitle>
              Meetup #{meetup.id} {meetupTitle(meetup)}
            </ItemTitle>
            <StatusPill status={meetup.status} />
            {candidate.source ? <SourcePill source={candidate.source} /> : null}
            <IntentPill intent={meetup.intent} />
            {meetup.plan_type ? <Pill>{prettyLabel(meetup.plan_type)}</Pill> : null}
          </TitleRow>
          <SummaryMeta>
            <div>Created {formatDateTime(meetup.created_at)}</div>
            <div>Meetup time {formatDateTime(meetup.meetup_time)}</div>
            <div>{locationLabel(meetup)}</div>
            <div>
              {(meetup.participant_summary && meetup.participant_summary.interested_count) || 0} interested
            </div>
          </SummaryMeta>
        </SummaryBody>
      </ItemSummary>

      <ItemBody>
        <MetaGrid>
          <MetaCard>
            <MetaLabel>Participants</MetaLabel>
            <MetaValue>
              <ParticipantSummary participants={meetup.participants || []} />
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Candidate</MetaLabel>
            <MetaValue>
              {candidate.id
                ? compactList([
                    `#${candidate.id}`,
                    prettyLabel(candidate.status),
                    prettyLabel(candidate.source),
                    `score ${candidate.arbitration_score || 0}`
                  ])
                : "No candidate row"}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Location</MetaLabel>
            <MetaValue>
              {compactList([
                locationLabel(meetup),
                meetup.location && meetup.location.location_type && prettyLabel(meetup.location.location_type)
              ])}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Copy</MetaLabel>
            <MetaValue>
              {compactList([meetup.teaser, meetup.ai_caption])}
            </MetaValue>
          </MetaCard>
        </MetaGrid>
        <SourcePlansSummary plans={meetup.source_plans || []} />
      </ItemBody>
    </ItemCard>
  );
};

const OpportunityItem = ({ item, defaultOpen }) => {
  const candidate = item.candidate || {};
  const meetup = item.meetup || {};
  return (
    <ItemCard open={defaultOpen}>
      <ItemSummary>
        <SummaryBody>
          <TitleRow>
            <ItemTitle>
              Candidate #{item.id} for meetup #{item.meetup_id}
            </ItemTitle>
            <StatusPill status={item.queue_state || candidate.status} />
            {candidate.source ? <SourcePill source={candidate.source} /> : null}
            {candidate.match_context ? <Pill>{prettyLabel(candidate.match_context)}</Pill> : null}
          </TitleRow>
          <SummaryMeta>
            <div>{meetupTitle(meetup)}</div>
            <div>Updated {formatDateTime(candidate.updated_at)}</div>
            <div>Surface until {formatDateTime(candidate.surface_expires_at)}</div>
            <div>Arbitration {candidate.arbitration_score || 0}</div>
          </SummaryMeta>
        </SummaryBody>
      </ItemSummary>

      <ItemBody>
        <MetaGrid>
          <MetaCard>
            <MetaLabel>Scores</MetaLabel>
            <MetaValue>
              {compactList([
                `compat ${candidate.compatibility_score || 0}`,
                `urgency ${candidate.urgency_score || 0}`,
                `actionability ${candidate.actionability_score || 0}`,
                `arbitration ${candidate.arbitration_score || 0}`
              ])}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Participants</MetaLabel>
            <MetaValue>
              <ParticipantSummary participants={meetup.participants || []} />
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Meetup</MetaLabel>
            <MetaValue>
              {compactList([
                meetupTitle(meetup),
                formatDateTime(meetup.meetup_time),
                locationLabel(meetup)
              ])}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Candidate timing</MetaLabel>
            <MetaValue>
              {compactList([
                candidate.surfaced_at ? `surfaced ${formatDateTime(candidate.surfaced_at)}` : null,
                candidate.confirmed_at ? `confirmed ${formatDateTime(candidate.confirmed_at)}` : null,
                candidate.dismissed_at ? `dismissed ${formatDateTime(candidate.dismissed_at)}` : null
              ])}
            </MetaValue>
          </MetaCard>
        </MetaGrid>
        <SourcePlansSummary plans={meetup.source_plans || []} />
      </ItemBody>
    </ItemCard>
  );
};

class LaterMatching extends React.Component {
  state = {
    reload: false,
    lastRefreshedAt: null,
    searchQuery: "",
    appliedSearchQuery: ""
  };

  loadDashboard = async () => {
    const dashboard = await api.later_matching.dashboard({
      recent_limit: RECENT_LIMIT,
      query: this.state.appliedSearchQuery || undefined
    });
    this.setState({ lastRefreshedAt: new Date() });
    return dashboard;
  };

  refresh = () => {
    this.setState(state => ({ reload: !state.reload }));
  };

  onSearchChange = event => {
    this.setState({ searchQuery: event.target.value });
  };

  onSearchKeyDown = event => {
    if (event.key === "Enter") {
      this.applySearch();
    }
  };

  applySearch = () => {
    this.setState(state => ({
      appliedSearchQuery: state.searchQuery.trim(),
      reload: !state.reload
    }));
  };

  clearSearch = () => {
    this.setState(state => ({
      searchQuery: "",
      appliedSearchQuery: "",
      reload: !state.reload
    }));
  };

  renderOverview = overview => {
    const cards = [
      { label: "Later AI meetups", value: overview.later_meetups_total },
      { label: "Created last 24h", value: overview.later_created_last_24h },
      { label: "Created last 7d", value: overview.later_created_last_7d },
      { label: "Active later meetups", value: overview.later_active_count },
      { label: "Later opportunities", value: overview.later_opportunity_count },
      { label: "Queued", value: overview.later_queued_count },
      { label: "Surfaced", value: overview.later_surfaced_count },
      { label: "Interested people", value: overview.later_interested_participant_count },
      { label: "Signal plans", value: overview.signal_plan_count },
      { label: "Profile signals", value: overview.profile_signal_plan_count },
      { label: "Trip signals", value: overview.trip_signal_plan_count },
      { label: "Seeded later", value: overview.seeded_later_plan_count },
      { label: "Organic plans", value: overview.organic_future_plan_count },
      { label: "Plans linked", value: overview.plans_with_linked_meetups },
      { label: "Linked AI meetups", value: overview.total_linked_ai_meetups }
    ];

    return (
      <SummaryGrid>
        {cards.map(card => (
          <SummaryCard key={card.label}>
            <SummaryLabel>{card.label}</SummaryLabel>
            <SummaryValue>{card.value || 0}</SummaryValue>
          </SummaryCard>
        ))}
      </SummaryGrid>
    );
  };

  renderSearch = () => {
    const { appliedSearchQuery, searchQuery } = this.state;
    return (
      <SearchBarRow>
        <SearchInput
          value={searchQuery}
          onChange={this.onSearchChange}
          onKeyDown={this.onSearchKeyDown}
          placeholder="Search users, plans, sources, metadata, or meetups"
        />
        <Button onClick={this.applySearch}>Search</Button>
        <Button onClick={this.clearSearch} disabled={!searchQuery && !appliedSearchQuery}>
          Clear
        </Button>
      </SearchBarRow>
    );
  };

  renderDashboard = dashboard => {
    const breakdowns = dashboard.breakdowns || {};
    const recentPlans = dashboard.recent_plans || [];
    const opportunityQueue = dashboard.opportunity_queue || [];
    const recentMeetups = dashboard.recent_meetups || [];
    const { appliedSearchQuery } = this.state;

    return (
      <React.Fragment>
        {this.renderOverview(dashboard.overview || {})}

        <SectionGrid>
          <BreakdownList title="Plan Source" items={breakdowns.by_plan_source || []} />
          <BreakdownList title="Active Plan Source" items={breakdowns.by_active_plan_source || []} />
          <BreakdownList title="Plan State" items={breakdowns.by_plan_state || []} />
          <BreakdownList title="Opportunity Source" items={breakdowns.by_opportunity_source || []} />
          <BreakdownList title="Opportunity Status" items={breakdowns.by_opportunity_status || []} />
          <BreakdownList title="Queue State" items={breakdowns.by_opportunity_queue_state || []} />
          <BreakdownList title="Meetup Intent (7d)" items={breakdowns.by_meetup_intent || []} />
          <BreakdownList title="Meetup Activity (7d)" items={breakdowns.by_meetup_activity || []} />
          <BreakdownList title="Meetup Plan Type (7d)" items={breakdowns.by_meetup_plan_type || []} />
          <BreakdownList title="Created Later Meetups (7d)" items={breakdowns.created_last_7d || []} />
        </SectionGrid>

        <SectionCard>
          <SectionTitle>Recent Later Source Plans</SectionTitle>
          {this.renderSearch()}
          {!recentPlans.length ? (
            <EmptyState>
              {appliedSearchQuery
                ? `No Later source plans found for "${appliedSearchQuery}".`
                : "No Later source plans found yet."}
            </EmptyState>
          ) : (
            <ItemList>
              {recentPlans.map((plan, index) => (
                <RecentPlanItem
                  key={plan.id}
                  plan={plan}
                  defaultOpen={index < 3 || plan.linked_ai_meetup_count > 0}
                />
              ))}
            </ItemList>
          )}
        </SectionCard>

        <SectionCard>
          <SectionTitle>Later Opportunity Queue</SectionTitle>
          {!opportunityQueue.length ? (
            <EmptyState>
              {appliedSearchQuery
                ? `No Later opportunities found for "${appliedSearchQuery}".`
                : "No Later opportunities found yet."}
            </EmptyState>
          ) : (
            <ItemList>
              {opportunityQueue.map((item, index) => (
                <OpportunityItem key={item.id} item={item} defaultOpen={index < 3} />
              ))}
            </ItemList>
          )}
        </SectionCard>

        <SectionCard>
          <SectionTitle>Recent Later AI Meetups</SectionTitle>
          {!recentMeetups.length ? (
            <EmptyState>
              {appliedSearchQuery
                ? `No Later AI meetups found for "${appliedSearchQuery}".`
                : "No Later AI meetups found yet."}
            </EmptyState>
          ) : (
            <ItemList>
              {recentMeetups.map((meetup, index) => (
                <RecentMeetupItem key={meetup.id} meetup={meetup} defaultOpen={index < 3} />
              ))}
            </ItemList>
          )}
        </SectionCard>
      </React.Fragment>
    );
  };

  render() {
    const { reload, lastRefreshedAt, appliedSearchQuery } = this.state;

    return (
      <Page title="Later Matching">
        <Toolbar>
          <ToolbarText>
            Snapshot of background Later matching: organic future plans, profile and trip signal plans,
            seeded later inventory, opportunity candidates, and created AI meetups.
            <Muted>
              {lastRefreshedAt
                ? `Last refreshed ${lastRefreshedAt.toLocaleString()}`
                : "Waiting for first refresh"}
              {appliedSearchQuery ? ` | Search: ${appliedSearchQuery}` : ""}
            </Muted>
          </ToolbarText>
          <ToolbarActions>
            <Button onClick={this.refresh}>Refresh now</Button>
          </ToolbarActions>
        </Toolbar>

        <Async fetchProps={this.loadDashboard} reload={reload}>
          {this.renderDashboard}
        </Async>
      </Page>
    );
  }
}

export default LaterMatching;
